import React, { useState, useEffect } from 'react';

const PagarmeConfig = ({ userData, loading: parentLoading }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [recebedorData, setRecebedorData] = useState(null);
    const [kycLink, setKycLink] = useState(null);
    const [qrCode, setQrCode] = useState(null);
    const [authorizationStatus, setAuthorizationStatus] = useState('pending');
    const [message, setMessage] = useState({ text: '', type: '' });

    // Dados do formulário
    const [formData, setFormData] = useState({
        // Dados pessoais obrigatórios
        nome: '',
        email: '',
        documento: '',
        tipo: 'individual',
        telefone: '',
        // Campos obrigatórios para PF
        data_nascimento: '',
        renda_mensal: '',
        ocupacao_profissional: '',
        // Endereço obrigatório
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
        ponto_referencia: '',
        // Dados bancários obrigatórios
        banco_codigo: '',
        agencia: '',
        conta: '',
        conta_dv: '',
        tipo_conta: 'checking',
        // Dados do titular
        titular_nome: '',
        titular_tipo: 'individual',
        titular_documento: ''
    });

    const [errors, setErrors] = useState({});

    const API_URL = process.env.REACT_APP_API;
    const isAuthor = ["autor", "admin"].includes(userData?.permissao);

    const bancos = [
        { codigo: "001", nome: "Banco do Brasil" },
        { codigo: "033", nome: "Santander" },
        { codigo: "104", nome: "Caixa Econômica Federal" },
        { codigo: "237", nome: "Bradesco" },
        { codigo: "341", nome: "Itaú" },
        { codigo: "260", nome: "Nubank" },
        { codigo: "077", nome: "Inter" },
        { codigo: "212", nome: "Original" },
        { codigo: "290", nome: "PagBank" },
        { codigo: "756", nome: "Sicoob" },
        { codigo: "748", nome: "Sicredi" }
    ];

    useEffect(() => {
        // Só carregar dados se não estiver carregando do parent e se for autor
        if (!parentLoading && isAuthor && userData) {
            loadUserData();
        }
    }, [parentLoading, isAuthor, userData]);

    // Função para mostrar mensagens
    const showMessage = (text, type = 'info') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const loadUserData = async () => {
        setLoading(true);
        try {
            // Pré-preencher alguns campos com dados do usuário
            setFormData(prev => ({
                ...prev,
                nome: userData.nome || '',
                email: userData.email || ''
            }));

            // Verificar se já tem recebedor cadastrado usando a rota correta
            await checkExistingRecebedor();
        } catch (error) {
            console.error('Erro ao carregar dados do usuário:', error);
            showMessage('Erro ao carregar dados do usuário', 'error');
        } finally {
            setLoading(false);
        }
    };

    const checkExistingRecebedor = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}api/usuario-editora/recebedor/dados`, {
            headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Dados do recebedor:', data);
                
                if (data.tem_recebedor && data.recebedor) {
                    setRecebedorData(data.recebedor);
                    setAuthorizationStatus(data.recebedor.status || 'pending');
                    
                    // Determinar em qual passo está baseado no status
                    if (data.recebedor.status === 'active') {
                        setStep(4); // Já configurado e ativo
                        showMessage('Configuração já concluída! Você pode receber pagamentos.', 'success');
                    } else if (data.recebedor.pagarme_recipient_id) {
                        setStep(3); // Tem recebedor mas precisa autorizar
                        showMessage('Recebedor criado. Complete a autorização para receber pagamentos.', 'warning');
                    } else {
                        setStep(2); // Dados salvos mas não enviados para Pagar.me
                        showMessage('Dados encontrados. Revise antes de criar no Pagar.me.', 'info');
                    }
                } else {
                    setStep(1); // Primeira configuração
                    showMessage('Configure seus dados para receber pagamentos via Pagar.me', 'info');
                }
            } else {
                console.error('Erro na resposta:', response.status);
                setStep(1);
            }
        } catch (error) {
            console.error('Erro ao verificar recebedor existente:', error);
            setStep(1);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Limpar erro do campo
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleDocumentoChange = (e) => {
        const valor = e.target.value.replace(/\D/g, '');
        if (valor.length <= 14) {
            setFormData(prev => ({
                ...prev,
                documento: valor
            }));
        }
    };

    const formatarDocumento = (valor) => {
        const numeros = valor.replace(/\D/g, '');
        
        if (numeros.length <= 11) {
            return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        } else {
            return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Dados pessoais obrigatórios
        if (!formData.nome.trim()) newErrors.nome = 'Nome é obrigatório';
        if (!formData.email.trim()) newErrors.email = 'Email é obrigatório';
        if (!formData.documento.trim()) newErrors.documento = 'Documento é obrigatório';
        
        // Campos obrigatórios para PF
        if (formData.tipo === 'individual') {
            if (!formData.data_nascimento) newErrors.data_nascimento = 'Data de nascimento é obrigatória';
            if (!formData.renda_mensal || formData.renda_mensal <= 0) newErrors.renda_mensal = 'Renda mensal deve ser maior que zero';
            if (!formData.ocupacao_profissional.trim()) newErrors.ocupacao_profissional = 'Ocupação profissional é obrigatória';
        }
        
        // Endereço obrigatório
        if (!formData.endereco.trim()) newErrors.endereco = 'Endereço é obrigatório';
        if (!formData.numero.trim()) newErrors.numero = 'Número é obrigatório';
        if (!formData.bairro.trim()) newErrors.bairro = 'Bairro é obrigatório';
        if (!formData.cidade.trim()) newErrors.cidade = 'Cidade é obrigatória';
        if (!formData.estado.trim()) newErrors.estado = 'Estado é obrigatório';
        if (!formData.cep.trim()) newErrors.cep = 'CEP é obrigatório';
        
        // Dados bancários obrigatórios
        if (!formData.banco_codigo) newErrors.banco_codigo = 'Banco é obrigatório';
        if (!formData.agencia.trim()) newErrors.agencia = 'Agência é obrigatória';
        if (!formData.conta.trim()) newErrors.conta = 'Conta é obrigatória';
        if (!formData.conta_dv.trim()) newErrors.conta_dv = 'Dígito da conta é obrigatório';
        if (!formData.titular_nome.trim()) newErrors.titular_nome = 'Nome do titular é obrigatório';
        if (!formData.titular_documento.trim()) newErrors.titular_documento = 'Documento do titular é obrigatório';

        // Validações específicas
        if (formData.email && !formData.email.includes('@')) {
            newErrors.email = 'Email inválido';
        }

        if (formData.documento) {
            const doc = formData.documento.replace(/\D/g, '');
            if (doc.length < 11 || (doc.length > 11 && doc.length < 14)) {
                newErrors.documento = 'Documento deve ter 11 (CPF) ou 14 (CNPJ) dígitos';
            }
        }
        
        // Validação de idade (maior de 18 anos)
        if (formData.data_nascimento) {
            const nascimento = new Date(formData.data_nascimento);
            const idade = (new Date() - nascimento) / (365.25 * 24 * 60 * 60 * 1000);
            if (idade < 18) {
                newErrors.data_nascimento = 'Você deve ser maior de 18 anos';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const createRecebedor = async () => {
        if (!validateForm()) return;

    let dadosRecebedor;
    setLoading(true);
    
    try {
        const token = localStorage.getItem('token');
        
        // Verificar se tem token
        if (!token) {
            throw new Error('Token de autenticação não encontrado. Faça login novamente.');
        }
        
        dadosRecebedor = {
            ...formData,
            documento: formData.documento.replace(/\D/g, ''),
            titular_documento: formData.titular_documento.replace(/\D/g, ''), // Limpar documento do titular também
            cep: formData.cep.replace(/\D/g, ''), // Limpar CEP
            usuario_editora_id: userData.id
        };

        console.log('🔍 DADOS ENVIADOS:', dadosRecebedor);

        const response = await fetch(`${API_URL}api/usuario-editora/recebedor/criar`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` // ← DESCOMENTADO
            },
            body: JSON.stringify(dadosRecebedor)
        });

        // LOG DETALHADO DA RESPOSTA
        console.log('📊 STATUS DA RESPOSTA:', {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            url: response.url
        });

        // CAPTURAR O TEXTO DA RESPOSTA PRIMEIRO
        const responseText = await response.text();
        console.log('📄 RESPOSTA BRUTA (primeiros 500 chars):', responseText.substring(0, 500));

        // VERIFICAR SE É JSON VÁLIDO
        let data;
        try {
            data = JSON.parse(responseText);
            console.log('📥 DADOS RECEBIDOS DA API:', data);
        } catch (jsonError) {
            console.error('❌ ERRO AO PARSEAR JSON:', {
                error: jsonError.message,
                responseStatus: response.status,
                responseText: responseText.substring(0, 1000)
            });
            
            throw new Error(`Servidor retornou resposta inválida (Status: ${response.status}). Verifique o console para detalhes.`);
        }

        if (response.ok && data.success) {
            setRecebedorData(data.data.recebedor); // ← CORRIGIDO: data.data.recebedor
            showMessage('Recebedor criado com sucesso!', 'success');
            
            if (data.data.recebedor.pagarme_recipient_id) {
                await generateKycLink(data.data.recebedor.pagarme_recipient_id); // Comentar aqui qualquer coisa dnv.
                setStep(3);
            } else {
                showMessage('Recebedor criado localmente. Será sincronizado com Pagar.me em breve.', 'warning');
                setStep(3);
            }
        } else {
            // Tratamento de erro melhorado
            console.error('❌ ERRO DA API:', {
                httpStatus: response.status,
                apiResponse: data,
                fullError: data
            });
            
            let errorMessage = data?.message || data?.error || `Erro HTTP ${response.status}: ${response.statusText}`;
            
            // Tratamento específico para erros de validação
            if (data?.details && typeof data.details === 'object') {
                const validationErrors = Object.entries(data.details).map(
                    ([field, messages]) => {
                        const msgArray = Array.isArray(messages) ? messages : [messages];
                        return `${field}: ${msgArray.join(', ')}`;
                    }
                ).join(' | ');
                errorMessage += ` - Detalhes: ${validationErrors}`;
            }
            
            showMessage(errorMessage, 'error');
        }
        
    } catch (error) {
        const completeErrorInfo = {
            errorName: error.name,
            errorMessage: error.message,
            dadosEnviados: dadosRecebedor,
            timestamp: new Date().toISOString(),
            hasToken: !!localStorage.getItem('token'),
            apiUrl: `${API_URL}api/recebedor/criar`
        };
        
        console.error('💥 ERRO COMPLETO:', completeErrorInfo);
        
        let userErrorMessage = 'Erro ao criar recebedor. Tente novamente.';
        
        if (error.message.includes('Token de autenticação')) {
            userErrorMessage = 'Sessão expirada. Faça login novamente.';
        } else if (error.message.includes('resposta inválida')) {
            userErrorMessage = 'Erro no servidor. Verifique o console e contate o suporte.';
        } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
            userErrorMessage = 'Erro de conexão com o servidor. Verifique sua internet.';
        }
        
        showMessage(userErrorMessage, 'error');
        
    } finally {
        setLoading(false);
    }
    };
//     const generateKycLink = async (recipientId) => {
//     if (!recipientId) {
//         showMessage('ID do recebedor não informado.', 'error');
//         return;
//     }

//     setLoading(true);

//     try {
//         const token = localStorage.getItem('token');
//         console.log('🔑 Token obtido:', token ? token.substring(0, 20) + '...' : 'NENHUM TOKEN');

//         const url = `${API_URL}pagarme/gerar-kyc-link`;
//         console.log('🌐 Requisição GET para:', url, 'com recipientId:', recipientId);

//         // Enviar POST com JSON contendo recipient_id, conforme o backend espera (rota POST)
//         const response = await fetch(url, {
//             method: 'POST',
//             headers: {
//                 'Content-Type': 'application/json',
//                 'Authorization': `Bearer ${token}`
//             },
//             body: JSON.stringify({ recipient_id: recipientId })
//         });

//         console.log('📊 Status HTTP:', response.status, response.statusText);

//         const responseText = await response.text();
//         console.log('📄 Resposta bruta:', responseText.substring(0, 500));

//         let data;
//         try {
//             data = JSON.parse(responseText);
//             console.log('📥 JSON parseado:', data);
//         } catch (jsonError) {
//             console.error('❌ Falha ao fazer parse JSON:', jsonError, 'Texto recebido:', responseText);
//             showMessage('Resposta inválida do servidor.', 'error');
//             return;
//         }

//         if (response.ok && data.success) {
//             setKycLink(data.data.url);
//             setQrCode(data.data.base64_qrcode || null);
//             showMessage('Link de autorização gerado com sucesso!', 'success');
//         } else {
//             console.error('❌ Erro da API ao gerar link KYC:', data);
//             showMessage(data.error || 'Erro ao gerar link KYC', 'error');
//         }

//     } catch (error) {
//         console.error('💥 Erro inesperado ao gerar link KYC:', error);
//         showMessage('Erro inesperado ao gerar link KYC', 'error');
//     } finally {
//         setLoading(false);
//         console.log('🏁 generateKycLink finalizado');
//     }
// };

const generateKycLink = async (recipientId) => { // Nova função frontend para gerar link KYC
    if (!recipientId) {
        showMessage('ID do recebedor não informado.', 'error');
        return;
    }

    setLoading(true);

    try {
        const token = localStorage.getItem('token');
        console.log('🔑 Token obtido:', token ? token.substring(0, 20) + '...' : 'NENHUM TOKEN');
        const url = `${API_URL}pagarme/gerar-kyc-link`;
        console.log('🌐 Requisição POST para:', url, 'com recipientId:', recipientId);

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ recipient_id: recipientId })
        });

        console.log('📊 Status HTTP:', response.status, response.statusText);

        const responseText = await response.text();
        console.log('📄 Resposta bruta:', responseText.substring(0, 500));

        let data;
        try {
            data = JSON.parse(responseText);
            console.log('📥 JSON parseado:', data);
        } catch (jsonError) {
            console.error('❌ Falha ao fazer parse JSON:', jsonError, 'Texto recebido:', responseText);
            showMessage('Resposta inválida do servidor.', 'error');
            return;
        }

        // Exibe SEMPRE o link recebido, seja ele "novo" ou "já existente"
        if (response.ok && data.success && data.data && data.data.url) {
            setKycLink(data.data.url);
            setQrCode(data.data.base64_qrcode || null);
            if (data.message && data.message.includes('existente')) {
                showMessage('Já existe um link de autorização válido. Confira abaixo!', 'info');
            } else {
                showMessage('Link de autorização gerado com sucesso!', 'success');
            }
        } else {
            console.error('❌ Erro da API ao gerar link KYC:', data);
            showMessage(data.error || 'Erro ao gerar link KYC', 'error');
        }
    } catch (error) {
        console.error('💥 Erro inesperado ao gerar link KYC:', error);
        showMessage('Erro inesperado ao gerar link KYC', 'error');
    } finally {
        setLoading(false);
        console.log('🏁 generateKycLink finalizado');
    }
};


//     const generateKycLink = async (recipientId) => {
//     console.log('🚀 INICIANDO generateKycLink:', { recipientId });
    
//     setLoading(true);
    
//     try {
//         const token = localStorage.getItem('token');
        
//         // DEBUG: Verificar dados de entrada
//         console.log('🔍 DADOS DE ENTRADA:', {
//             recipientId: recipientId,
//             recipientIdType: typeof recipientId,
//             hasToken: !!token,
//             tokenPreview: token ? `${token.substring(0, 20)}...` : null,
//             apiUrl: `${API_URL}pagarme/gerar-kyc-link`
//         });

//         // Validação de entrada
//         if (!recipientId) {
//             throw new Error('recipientId é obrigatório para gerar KYC link');
//         }

//         if (!token) {
//             throw new Error('Token de autenticação não encontrado');
//         }

//         const requestBody = { recipient_id: recipientId };
//         console.log('📤 DADOS ENVIADOS:', requestBody);

//         const response = await fetch(`${API_URL}pagarme/gerar-kyc-link`, {
//             method: 'POST',
//             headers: {
//                 'Content-Type': 'application/json',
//                 'Authorization': `Bearer ${token}`
//             },
//             body: JSON.stringify(requestBody)
//         });

//         // DEBUG COMPLETO DA RESPOSTA
//         console.log('📊 RESPOSTA HTTP:', {
//             status: response.status,
//             statusText: response.statusText,
//             ok: response.ok,
//             url: response.url,
//             headers: {
//                 contentType: response.headers.get('content-type'),
//                 contentLength: response.headers.get('content-length')
//             }
//         });

//         // CAPTURAR TEXTO DA RESPOSTA PRIMEIRO
//         const responseText = await response.text();
//         console.log('📄 RESPOSTA BRUTA:', {
//             length: responseText.length,
//             preview: responseText.substring(0, 500),
//             isHTML: responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')
//         });

//         // PARSEAR JSON
//         let data;
//         try {
//             data = JSON.parse(responseText);
//             console.log('📥 DADOS PARSEADOS:', data);
//         } catch (jsonError) {
//             console.error('❌ ERRO JSON PARSE:', {
//                 error: jsonError.message,
//                 responseStatus: response.status,
//                 responseText: responseText.substring(0, 1000)
//             });

//             // Se não conseguiu parsear JSON, provavelmente é HTML de erro
//             if (responseText.includes('<!DOCTYPE') || responseText.includes('<html')) {
//                 throw new Error(`Servidor retornou HTML em vez de JSON (Status: ${response.status}). Rota pode não existir.`);
//             } else {
//                 throw new Error(`Resposta não é JSON válido: ${jsonError.message}`);
//             }
//         }

//         // ANÁLISE DA RESPOSTA DE SUCESSO
//         if (response.ok && data.success) {
//             console.log('✅ SUCESSO - KYC LINK GERADO:', {
//                 hasUrl: !!data.url,
//                 hasQrCode: !!data.base64_qrcode,
//                 url: data.url,
//                 qrCodeLength: data.base64_qrcode ? data.base64_qrcode.length : 0,
//                 additionalData: { ...data, url: '[HIDDEN]', base64_qrcode: '[HIDDEN]' }
//             });

//             setKycLink(data.url);
//             setQrCode(data.base64_qrcode);
//             showMessage('Link de autorização gerado com sucesso!', 'success');
            
//         } else {
//             // ANÁLISE DETALHADA DO ERRO
//             console.error('❌ ERRO DA API KYC:', {
//                 httpStatus: response.status,
//                 httpStatusText: response.statusText,
//                 apiSuccess: data?.success,
//                 apiMessage: data?.message,
//                 apiError: data?.error,
//                 apiErrors: data?.errors,
//                 fullApiResponse: data
//             });

//             let errorMessage = data?.message || data?.error || `Erro HTTP ${response.status}: ${response.statusText}`;
            
//             // Tratamento de erros específicos
//             if (response.status === 401) {
//                 errorMessage = 'Token de autenticação inválido ou expirado';
//             } else if (response.status === 403) {
//                 errorMessage = 'Sem permissão para gerar link KYC';
//             } else if (response.status === 404) {
//                 errorMessage = 'Rota de geração de KYC não encontrada';
//             } else if (response.status === 422 && data?.errors) {
//                 const validationErrors = Object.entries(data.errors).map(
//                     ([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
//                 ).join(' | ');
//                 errorMessage += ` - Detalhes: ${validationErrors}`;
//             }

//             showMessage(errorMessage, 'error');
//         }

//     } catch (error) {
//         // DEBUG COMPLETO DO ERRO
//         const completeErrorInfo = {
//             errorName: error.name,
//             errorMessage: error.message,
//             errorStack: error.stack,
//             recipientId: recipientId,
//             timestamp: new Date().toISOString(),
//             hasToken: !!localStorage.getItem('token'),
//             apiUrl: `${API_URL}pagarme/gerar-kyc-link`,
//             userAgent: navigator.userAgent
//         };
        
//         console.error('💥 ERRO COMPLETO generateKycLink:', completeErrorInfo);
        
//         // MENSAGENS ESPECÍFICAS PARA O USUÁRIO
//         let userErrorMessage = 'Erro ao gerar link de autorização';
        
//         if (error.message.includes('recipientId é obrigatório')) {
//             userErrorMessage = 'ID do recebedor não fornecido. Tente recriar o recebedor.';
//         } else if (error.message.includes('Token de autenticação não encontrado')) {
//             userErrorMessage = 'Sessão expirada. Faça login novamente.';
//         } else if (error.message.includes('Servidor retornou HTML')) {
//             userErrorMessage = 'Rota de autorização não encontrada. Contate o suporte.';
//         } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
//             userErrorMessage = 'Erro de conexão. Verifique sua internet.';
//         } else if (error.message.includes('JSON válido')) {
//             userErrorMessage = 'Resposta inválida do servidor. Tente novamente.';
//         }
        
//         showMessage(userErrorMessage, 'error');
        
//     } finally {
//         console.log('🏁 generateKycLink FINALIZADO');
//         setLoading(false);
//     }
// };
    const checkAuthorizationStatus = async () => {
        if (!recebedorData?.pagarme_recipient_id) {
            showMessage('ID do recebedor não encontrado', 'error');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}pagarme/verificar-status/${recebedorData.pagarme_recipient_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setAuthorizationStatus(data.status);
                setRecebedorData(prev => ({ ...prev, status: data.status }));
                
                if (data.status === 'active') {
                    setStep(4);
                    showMessage('✅ Autorização completada com sucesso! Você já pode receber pagamentos.', 'success');
                } else {
                    showMessage(`Status atual: ${data.status}. Complete a autorização para continuar.`, 'warning');
                }
            } else {
                showMessage(data.message || 'Erro ao verificar status', 'error');
            }
        } catch (error) {
            console.error('Erro ao verificar status:', error);
            showMessage('Erro ao verificar status da autorização', 'error');
        } finally {
            setLoading(false);
        }
    };

    const [pixTeste, setPixTeste] = useState(null);

    const gerarPixTeste = async () => {
        if (!recebedorData?.pagarme_recipient_id) {
            showMessage('Recebedor não configurado', 'error');
            return;
        }

        if (authorizationStatus !== 'active') {
            showMessage('Complete a autorização antes de gerar PIX de teste', 'warning');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}api/usuario-editora/recebedor/teste`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setPixTeste(data.data);
                showMessage('🎯 PIX de teste gerado! Split: 10% Jacques, 50% Editora, 40% Você (R$ 2,00)', 'success');
            } else {
                showMessage(data.error || 'Erro ao gerar PIX de teste', 'error');
            }
        } catch (error) {
            console.error('Erro ao gerar PIX de teste:', error);
            showMessage('Erro ao gerar PIX de teste', 'error');
        } finally {
            setLoading(false);
        }
    };

    const copyPixCode = () => {
        if (pixTeste?.charges?.[0]?.last_transaction?.qr_code) {
            navigator.clipboard.writeText(pixTeste.charges[0].last_transaction.qr_code);
            showMessage('Código PIX copiado!', 'success');
        }
    };
    const sincronizarRecebedor = async () => { // Nova forma de implementar o método de sincronizar recebedor.
    if (!recebedorData?.pagarme_recipient_id) {
        showMessage('Recebedor não configurado', 'error');
        return;
    }

    setLoading(true);

    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}pagarme/sincronizar-recebedor`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                recipient_id: recebedorData.pagarme_recipient_id
            })
        });

        // DEBUG: Verificar HTTP status antes de parsear JSON
        console.log("STATUS HTTP: ", response.status);

        let data = null;
        try {
            data = await response.json();
            console.log("RESP JSON:", data);
        } catch (jsonError) {
            showMessage('Resposta inválida do servidor de sincronização.', 'error');
            setLoading(false);
            return;
        }

        if (response.ok && data.success && data.recebedor) {
            // Usa sempre o status em lowercase para evitar bugs de comparação (ex: 'Active', 'ACTIVE', etc.)
            const status = String(data.recebedor.status || '').toLowerCase();

            setRecebedorData(data.recebedor);
            setAuthorizationStatus(status);

            showMessage('🔄 Dados sincronizados com sucesso!', 'success');

            if (status === 'active') {
                setStep(4);
                showMessage('✅ Recebedor está ATIVO no Pagar.me! Pronto para receber pagamentos.', 'success');
            } else {
                showMessage(`Status atualizado para: ${status}. Verifique se a ativação está pendente no painel do Pagar.me.`, 'warning');
            }

        } else if (data) {
            showMessage(data.message || data.error || 'Erro ao sincronizar dados', 'error');
            // EXTRA: Mostre detalhes para debug se precisar
            if (data.recebedor) {
                // Mostra o status retornado brute force para debug
                console.warn('Retorno recebedor backend:', data.recebedor);
            }
        } else {
            showMessage('Erro imprevisto ao sincronizar dados', 'error');
        }

    } catch (error) {
        console.error('Erro ao sincronizar:', error);
        showMessage('Erro de comunicação com o servidor', 'error');
    } finally {
        setLoading(false);
    }
};


    const editarDados = () => {
        // Pré-preencher formulário com dados existentes
        if (recebedorData) {
            setFormData({
                nome: recebedorData.nome || '',
                email: recebedorData.email || '',
                documento: recebedorData.documento || '',
                tipo: recebedorData.tipo === 'autor' ? 'individual' : 'corporation',
                telefone: recebedorData.telefone || '',
                banco_codigo: recebedorData.banco_codigo || '',
                agencia: recebedorData.agencia || '',
                conta: recebedorData.conta || '',
                conta_dv: recebedorData.conta_dv || '',
                tipo_conta: recebedorData.tipo_conta || 'checking'
            });
        }
        setStep(1);
        showMessage('Editando dados. Faça as alterações necessárias.', 'info');
    };

    const getBancoNome = (codigo) => {
        const banco = bancos.find(b => b.codigo === codigo);
        return banco ? banco.nome : `Banco ${codigo}`;
    };

    const nextStep = () => {
        if (step === 1) {
            if (recebedorData) {
                // Se já tem recebedor, apenas atualizar
                updateRecebedor();
            } else {
                // Criar novo recebedor
                createRecebedor();
            }
        } else if (step === 2) {
            setStep(3);
        } else if (step < 4) {
            setStep(step + 1);
        }
    };

    const prevStep = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    };

    const updateRecebedor = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const dadosRecebedor = {
                ...formData,
                documento: formData.documento.replace(/\D/g, ''),
            };

            const response = await fetch(`${API_URL}/pagarme/atualizar-recebedor/${recebedorData.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(dadosRecebedor)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setRecebedorData(data.recebedor);
                showMessage('Dados atualizados com sucesso!', 'success');
                setStep(3);
            } else {
                showMessage(data.message || 'Erro ao atualizar dados', 'error');
            }
        } catch (error) {
            console.error('Erro ao atualizar recebedor:', error);
            showMessage('Erro ao atualizar dados. Tente novamente.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Se ainda está carregando dados do parent
    if (parentLoading) {
        return (
            <div className="container-fluid py-4">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-8">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary mb-3" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <h5 className="card-title text-muted">Carregando dados do usuário...</h5>
                                <p className="card-text text-muted">Por favor, aguarde um momento</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Se não é autor
    if (!isAuthor) {
        return (
            <div className="container-fluid py-4">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-8">
                        <div className="alert alert-warning border-0 shadow-sm" role="alert">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className="fas fa-exclamation-triangle fa-2x text-warning"></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h4 className="alert-heading">❌ Acesso Restrito</h4>
                                    <p className="mb-2">Esta seção é exclusiva para autores.</p>
                                    <p className="mb-0">Solicite ser autor na aba "Solicitar ser Autor" para ter acesso a esta funcionalidade.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="row justify-content-center">
                <div className="col-12 col-xl-10">
                    {/* Header */}
                    <div className="mb-4">
                        <div className="text-center mb-4">
                            <h1 className="display-6 fw-bold text-primary mb-2">
                                <i className="fas fa-credit-card me-2"></i>
                                Configuração Pagar.me
                            </h1>
                            <p className="lead text-muted">Configure seus dados para receber pagamentos via split</p>
                        </div>
                        
                        {/* Status atual */}
                        {recebedorData && (
                            <div className="card border-0 shadow-sm mb-4">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col-md-8">
                                            <h6 className="card-title mb-2 fw-bold">
                                                <i className="fas fa-info-circle text-primary me-2"></i>
                                                Status atual:
                                            </h6>
                                            <span className={`badge ${
                                                authorizationStatus === 'active' ? 'bg-success' :
                                                authorizationStatus === 'pending' ? 'bg-warning text-dark' :
                                                authorizationStatus === 'review' ? 'bg-info' : 'bg-danger'
                                            } fs-6 px-3 py-2`}>
                                                {authorizationStatus === 'pending' && '⏳ Aguardando autorização'}
                                                {authorizationStatus === 'active' && '✅ Ativo - Pode receber pagamentos'}
                                                {authorizationStatus === 'inactive' && '❌ Inativo'}
                                                {authorizationStatus === 'review' && '🔍 Em análise'}
                                            </span>
                                        </div>
                                        {recebedorData.pagarme_recipient_id && (
                                            <div className="col-md-4 text-md-end">
                                                <small className="text-muted">
                                                    <strong>ID Pagar.me:</strong><br/> 
                                                    <code className="text-primary">{recebedorData.pagarme_recipient_id}</code>
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sistema de mensagens */}
                    {message.text && (
                        <div className={`alert ${
                            message.type === 'success' ? 'alert-success' :
                            message.type === 'error' ? 'alert-danger' :
                            message.type === 'warning' ? 'alert-warning' :
                            'alert-info'
                        } alert-dismissible border-0 shadow-sm mb-4`} role="alert">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0 me-3">
                                    <i className={`fas ${
                                        message.type === 'success' ? 'fa-check-circle text-success' :
                                        message.type === 'error' ? 'fa-exclamation-circle text-danger' :
                                        message.type === 'warning' ? 'fa-exclamation-triangle text-warning' :
                                        'fa-info-circle text-info'
                                    } fa-lg`}></i>
                                </div>
                                <div className="flex-grow-1">
                                    <span className="fw-medium">{message.text}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Progress Indicator */}
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body">
                            <div className="row text-center">
                                <div className="col-3">
                                    <div className={`position-relative ${step >= 1 ? 'text-primary' : 'text-muted'}`}>
                                        <div className={`rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center ${
                                            step > 1 ? 'bg-success text-white' : 
                                            step === 1 ? 'bg-primary text-white' : 'bg-light text-muted'
                                        }`} style={{width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold'}}>
                                            {step > 1 ? '✓' : '1'}
                                        </div>
                                        <small className="fw-medium">Dados Pessoais</small>
                                    </div>
                                </div>
                                <div className="col-3">
                                    <div className={`position-relative ${step >= 2 ? 'text-primary' : 'text-muted'}`}>
                                        <div className={`rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center ${
                                            step > 2 ? 'bg-success text-white' : 
                                            step === 2 ? 'bg-primary text-white' : 'bg-light text-muted'
                                        }`} style={{width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold'}}>
                                            {step > 2 ? '✓' : '2'}
                                        </div>
                                        <small className="fw-medium">Revisar</small>
                                    </div>
                                </div>
                                <div className="col-3">
                                    <div className={`position-relative ${step >= 3 ? 'text-primary' : 'text-muted'}`}>
                                        <div className={`rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center ${
                                            step > 3 ? 'bg-success text-white' : 
                                            step === 3 ? 'bg-primary text-white' : 'bg-light text-muted'
                                        }`} style={{width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold'}}>
                                            {step > 3 ? '✓' : '3'}
                                        </div>
                                        <small className="fw-medium">Autorização</small>
                                    </div>
                                </div>
                                <div className="col-3">
                                    <div className={`position-relative ${step >= 4 ? 'text-primary' : 'text-muted'}`}>
                                        <div className={`rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center ${
                                            step === 4 ? 'bg-success text-white' : 'bg-light text-muted'
                                        }`} style={{width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold'}}>
                                            {step === 4 ? '✓' : '4'}
                                        </div>
                                        <small className="fw-medium">Concluído</small>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="progress mt-3" style={{height: '6px'}}>
                                <div 
                                    className="progress-bar bg-primary" 
                                    role="progressbar" 
                                    style={{width: `${(step - 1) * 33.33}%`}}
                                    aria-valuenow={step} 
                                    aria-valuemin="1" 
                                    aria-valuemax="4"
                                ></div>
                            </div>
                        </div>
                    </div>

                    {/* Step 1: Dados Pessoais */}
                    {step === 1 && (
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-primary text-white">
                                <h3 className="card-title mb-0">
                                    <i className="fas fa-user me-2"></i>
                                    Dados Pessoais e Bancários
                                </h3>
                            </div>
                            <div className="card-body">
                                {/* Explicação do que está acontecendo */}
                                {recebedorData ? (
                                    <div className="alert alert-info border-0 mb-4">
                                        <div className="d-flex align-items-start">
                                            <div className="flex-shrink-0 me-3">
                                                <i className="fas fa-edit fa-2x text-info"></i>
                                            </div>
                                            <div className="flex-grow-1">
                                                <h5 className="alert-heading">Editando seus dados</h5>
                                                <p className="mb-0">
                                                    Você já tem um recebedor configurado. Revise e atualize suas informações 
                                                    se necessário. Após salvar, você poderá prosseguir para a autorização.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="alert alert-primary border-0 mb-4">
                                        <div className="d-flex align-items-start">
                                            <div className="flex-shrink-0 me-3">
                                                <i className="fas fa-rocket fa-2x text-primary"></i>
                                            </div>
                                            <div className="flex-grow-1">
                                                <h5 className="alert-heading">Primeira configuração</h5>
                                                <p className="mb-0">
                                                    Vamos configurar sua conta para receber pagamentos via Pagar.me. 
                                                    Preencha seus dados pessoais e bancários com cuidado.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Dados Pessoais */}
                                <div className="mb-5">
                                    <h5 className="mb-3 text-primary">
                                        <i className="fas fa-user-circle me-2"></i>
                                        Informações Pessoais
                                    </h5>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">Nome Completo *</label>
                                            <input
                                                type="text"
                                                name="nome"
                                                value={formData.nome}
                                                onChange={handleInputChange}
                                                placeholder="Seu nome completo"
                                                maxLength="30"
                                                className={`form-control ${errors.nome ? 'is-invalid' : ''}`}
                                            />
                                            {errors.nome && <div className="invalid-feedback">{errors.nome}</div>}
                                            <small className="form-text text-muted">Máximo 30 caracteres (limitação do Pagar.me)</small>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">Email *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="seu@email.com"
                                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                            />
                                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">CPF/CNPJ *</label>
                                            <input
                                                type="text"
                                                name="documento"
                                                value={formatarDocumento(formData.documento)}
                                                onChange={handleDocumentoChange}
                                                placeholder="000.000.000-00"
                                                className={`form-control ${errors.documento ? 'is-invalid' : ''}`}
                                            />
                                            {errors.documento && <div className="invalid-feedback">{errors.documento}</div>}
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label fw-medium">Tipo</label>
                                            <select
                                                name="tipo"
                                                value={formData.tipo}
                                                onChange={handleInputChange}
                                                className="form-select"
                                            >
                                                <option value="individual">Pessoa Física</option>
                                                <option value="corporation">Pessoa Jurídica</option>
                                            </select>
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label fw-medium">Telefone</label>
                                            <input
                                                type="text"
                                                name="telefone"
                                                value={formData.telefone}
                                                onChange={handleInputChange}
                                                placeholder="(11) 99999-9999"
                                                className="form-control"
                                            />
                                        </div>
                                        
                                        {/* Campos obrigatórios para PF */}
                                        {formData.tipo === 'individual' && (
                                            <>
                                                <div className="col-md-4">
                                                    <label className="form-label fw-medium">Data de Nascimento *</label>
                                                    <input
                                                        type="date"
                                                        name="data_nascimento"
                                                        value={formData.data_nascimento}
                                                        onChange={handleInputChange}
                                                        className={`form-control ${errors.data_nascimento ? 'is-invalid' : ''}`}
                                                    />
                                                    {errors.data_nascimento && <div className="invalid-feedback">{errors.data_nascimento}</div>}
                                                </div>
                                                
                                                <div className="col-md-4">
                                                    <label className="form-label fw-medium">Renda Mensal (R$) *</label>
                                                    <input
                                                        type="number"
                                                        name="renda_mensal"
                                                        value={formData.renda_mensal}
                                                        onChange={handleInputChange}
                                                        placeholder="Ex: 3000.00"
                                                        step="0.01"
                                                        min="0"
                                                        className={`form-control ${errors.renda_mensal ? 'is-invalid' : ''}`}
                                                    />
                                                    {errors.renda_mensal && <div className="invalid-feedback">{errors.renda_mensal}</div>}
                                                </div>
                                                
                                                <div className="col-md-12">
                                                    <label className="form-label fw-medium">Ocupação Profissional *</label>
                                                    <input
                                                        type="text"
                                                        name="ocupacao_profissional"
                                                        value={formData.ocupacao_profissional}
                                                        onChange={handleInputChange}
                                                        placeholder="Ex: Escritor, Professor, etc."
                                                        className={`form-control ${errors.ocupacao_profissional ? 'is-invalid' : ''}`}
                                                    />
                                                    {errors.ocupacao_profissional && <div className="invalid-feedback">{errors.ocupacao_profissional}</div>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Seção de Endereço */}
                                <div className="mb-5">
                                    <h5 className="mb-3 text-primary">
                                        <i className="fas fa-map-marker-alt me-2"></i>
                                        Endereço Completo
                                    </h5>
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <label className="form-label fw-medium">CEP *</label>
                                            <input
                                                type="text"
                                                name="cep"
                                                value={formData.cep}
                                                onChange={handleInputChange}
                                                placeholder="00000-000"
                                                maxLength="9"
                                                className={`form-control ${errors.cep ? 'is-invalid' : ''}`}
                                            />
                                            {errors.cep && <div className="invalid-feedback">{errors.cep}</div>}
                                        </div>
                                        
                                        <div className="col-md-4">
                                            <label className="form-label fw-medium">Estado *</label>
                                            <select
                                                name="estado"
                                                value={formData.estado}
                                                onChange={handleInputChange}
                                                className={`form-select ${errors.estado ? 'is-invalid' : ''}`}
                                            >
                                                <option value="">Selecione</option>
                                                <option value="AC">Acre</option>
                                                <option value="AL">Alagoas</option>
                                                <option value="AP">Amapá</option>
                                                <option value="AM">Amazonas</option>
                                                <option value="BA">Bahia</option>
                                                <option value="CE">Ceará</option>
                                                <option value="DF">Distrito Federal</option>
                                                <option value="ES">Espírito Santo</option>
                                                <option value="GO">Goiás</option>
                                                <option value="MA">Maranhão</option>
                                                <option value="MT">Mato Grosso</option>
                                                <option value="MS">Mato Grosso do Sul</option>
                                                <option value="MG">Minas Gerais</option>
                                                <option value="PA">Pará</option>
                                                <option value="PB">Paraíba</option>
                                                <option value="PR">Paraná</option>
                                                <option value="PE">Pernambuco</option>
                                                <option value="PI">Piauí</option>
                                                <option value="RJ">Rio de Janeiro</option>
                                                <option value="RN">Rio Grande do Norte</option>
                                                <option value="RS">Rio Grande do Sul</option>
                                                <option value="RO">Rondônia</option>
                                                <option value="RR">Roraima</option>
                                                <option value="SC">Santa Catarina</option>
                                                <option value="SP">São Paulo</option>
                                                <option value="SE">Sergipe</option>
                                                <option value="TO">Tocantins</option>
                                            </select>
                                            {errors.estado && <div className="invalid-feedback">{errors.estado}</div>}
                                        </div>
                                        
                                        <div className="col-md-4">
                                            <label className="form-label fw-medium">Cidade *</label>
                                            <input
                                                type="text"
                                                name="cidade"
                                                value={formData.cidade}
                                                onChange={handleInputChange}
                                                placeholder="São Paulo, Rio de Janeiro, etc."
                                                className={`form-control ${errors.cidade ? 'is-invalid' : ''}`}
                                            />
                                            {errors.cidade && <div className="invalid-feedback">{errors.cidade}</div>}
                                        </div>
                                        
                                        <div className="col-md-8">
                                            <label className="form-label fw-medium">Endereço *</label>
                                            <input
                                                type="text"
                                                name="endereco"
                                                value={formData.endereco}
                                                onChange={handleInputChange}
                                                placeholder="Rua, Avenida, etc."
                                                className={`form-control ${errors.endereco ? 'is-invalid' : ''}`}
                                            />
                                            {errors.endereco && <div className="invalid-feedback">{errors.endereco}</div>}
                                        </div>
                                        
                                        <div className="col-md-2">
                                            <label className="form-label fw-medium">Número *</label>
                                            <input
                                                type="text"
                                                name="numero"
                                                value={formData.numero}
                                                onChange={handleInputChange}
                                                placeholder="123"
                                                className={`form-control ${errors.numero ? 'is-invalid' : ''}`}
                                            />
                                            {errors.numero && <div className="invalid-feedback">{errors.numero}</div>}
                                        </div>
                                        
                                        <div className="col-md-2">
                                            <label className="form-label fw-medium">Complemento</label>
                                            <input
                                                type="text"
                                                name="complemento"
                                                value={formData.complemento}
                                                onChange={handleInputChange}
                                                placeholder="Apto 101"
                                                className="form-control"
                                            />
                                        </div>
                                        
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">Bairro *</label>
                                            <input
                                                type="text"
                                                name="bairro"
                                                value={formData.bairro}
                                                onChange={handleInputChange}
                                                placeholder="Centro, Vila Nova, etc."
                                                className={`form-control ${errors.bairro ? 'is-invalid' : ''}`}
                                            />
                                            {errors.bairro && <div className="invalid-feedback">{errors.bairro}</div>}
                                        </div>
                                        
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">Ponto de Referência</label>
                                            <input
                                                type="text"
                                                name="ponto_referencia"
                                                value={formData.ponto_referencia}
                                                onChange={handleInputChange}
                                                placeholder="Próximo ao mercado, etc."
                                                className="form-control"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Dados Bancários */}
                                <div>
                                    <h5 className="mb-3 text-primary">
                                        <i className="fas fa-university me-2"></i>
                                        Dados Bancários
                                    </h5>
                                    <div className="row g-3">
                                        <div className="col-md-12">
                                            <label className="form-label fw-medium">Banco *</label>
                                            <select
                                                name="banco_codigo"
                                                value={formData.banco_codigo}
                                                onChange={handleInputChange}
                                                className={`form-select ${errors.banco_codigo ? 'is-invalid' : ''}`}
                                            >
                                                <option value="">Selecione um banco</option>
                                                {bancos.map(banco => (
                                                    <option key={banco.codigo} value={banco.codigo}>
                                                        {banco.codigo} - {banco.nome}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.banco_codigo && <div className="invalid-feedback">{errors.banco_codigo}</div>}
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label fw-medium">Agência *</label>
                                            <input
                                                type="text"
                                                name="agencia"
                                                value={formData.agencia}
                                                onChange={handleInputChange}
                                                placeholder="0001"
                                                className={`form-control ${errors.agencia ? 'is-invalid' : ''}`}
                                            />
                                            {errors.agencia && <div className="invalid-feedback">{errors.agencia}</div>}
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label fw-medium">Número da Conta *</label>
                                            <input
                                                type="text"
                                                name="conta"
                                                value={formData.conta}
                                                onChange={handleInputChange}
                                                placeholder="1234567"
                                                className={`form-control ${errors.conta ? 'is-invalid' : ''}`}
                                            />
                                            {errors.conta && <div className="invalid-feedback">{errors.conta}</div>}
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label fw-medium">Dígito da Conta *</label>
                                            <input
                                                type="text"
                                                name="conta_dv"
                                                value={formData.conta_dv}
                                                onChange={handleInputChange}
                                                placeholder="1"
                                                maxLength="2"
                                                className={`form-control ${errors.conta_dv ? 'is-invalid' : ''}`}
                                            />
                                            {errors.conta_dv && <div className="invalid-feedback">{errors.conta_dv}</div>}
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label fw-medium">Tipo de Conta</label>
                                            <select
                                                name="tipo_conta"
                                                value={formData.tipo_conta}
                                                onChange={handleInputChange}
                                                className="form-select"
                                            >
                                                <option value="checking">Conta Corrente</option>
                                                <option value="savings">Poupança</option>
                                            </select>
                                        </div>
                                        
                                        {/* Dados do Titular */}
                                        <div className="col-md-12">
                                            <hr/>
                                            <h6 className="text-primary mb-3">
                                                <i className="fas fa-user-tie me-2"></i>
                                                Dados do Titular da Conta
                                            </h6>
                                        </div>
                                        
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">Nome do Titular *</label>
                                            <input
                                                type="text"
                                                name="titular_nome"
                                                value={formData.titular_nome}
                                                onChange={handleInputChange}
                                                placeholder="Nome completo"
                                                className={`form-control ${errors.titular_nome ? 'is-invalid' : ''}`}
                                            />
                                            {errors.titular_nome && <div className="invalid-feedback">{errors.titular_nome}</div>}
                                        </div>
                                        
                                        <div className="col-md-3">
                                            <label className="form-label fw-medium">Tipo do Titular *</label>
                                            <select
                                                name="titular_tipo"
                                                value={formData.titular_tipo}
                                                onChange={handleInputChange}
                                                className="form-select"
                                            >
                                                <option value="individual">Pessoa Física</option>
                                                <option value="company">Pessoa Jurídica</option>
                                            </select>
                                        </div>
                                        
                                        <div className="col-md-3">
                                            <label className="form-label fw-medium">Documento do Titular *</label>
                                            <input
                                                type="text"
                                                name="titular_documento"
                                                value={formData.titular_documento}
                                                onChange={handleInputChange}
                                                placeholder="000.000.000-00"
                                                className={`form-control ${errors.titular_documento ? 'is-invalid' : ''}`}
                                            />
                                            {errors.titular_documento && <div className="invalid-feedback">{errors.titular_documento}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Revisar */}
                    {step === 2 && (
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-info text-white">
                                <h3 className="card-title mb-0">
                                    <i className="fas fa-clipboard-check me-2"></i>
                                    Revisar Informações
                                </h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="border rounded p-3 h-100">
                                            <h5 className="text-primary mb-3">
                                                <i className="fas fa-user-circle me-2"></i>
                                                Dados Pessoais
                                            </h5>
                                            <div className="mb-3">
                                                <strong className="text-muted">Nome:</strong><br/>
                                                <span className="fs-6">{formData.nome}</span>
                                            </div>
                                            <div className="mb-3">
                                                <strong className="text-muted">Email:</strong><br/>
                                                <span className="fs-6">{formData.email}</span>
                                            </div>
                                            <div className="mb-3">
                                                <strong className="text-muted">Documento:</strong><br/>
                                                <span className="fs-6">{formatarDocumento(formData.documento)}</span>
                                            </div>
                                            <div className="mb-0">
                                                <strong className="text-muted">Tipo:</strong><br/>
                                                <span className="fs-6">{formData.tipo === 'individual' ? 'Pessoa Física' : 'Pessoa Jurídica'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="border rounded p-3 h-100">
                                            <h5 className="text-primary mb-3">
                                                <i className="fas fa-university me-2"></i>
                                                Dados Bancários
                                            </h5>
                                            <div className="mb-3">
                                                <strong className="text-muted">Banco:</strong><br/>
                                                <span className="fs-6">{getBancoNome(formData.banco_codigo)}</span>
                                            </div>
                                            <div className="mb-3">
                                                <strong className="text-muted">Agência:</strong><br/>
                                                <span className="fs-6">{formData.agencia}</span>
                                            </div>
                                            <div className="mb-3">
                                                <strong className="text-muted">Conta:</strong><br/>
                                                <span className="fs-6">{formData.conta}-{formData.conta_dv}</span>
                                            </div>
                                            <div className="mb-0">
                                                <strong className="text-muted">Tipo:</strong><br/>
                                                <span className="fs-6">{formData.tipo_conta === 'checking' ? 'Conta Corrente' : 'Poupança'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-warning border-0 mt-4">
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-exclamation-triangle text-warning me-3"></i>
                                        <div>
                                            <strong>Importante:</strong> Verifique todos os dados cuidadosamente. 
                                            Após a criação, algumas informações não poderão ser alteradas.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Autorização */}
                    {step === 3 && (
                        <div className="card border-0 shadow-sm">
                            <div className="card-header text-dark">
                                <h3 className="card-title mb-0">
                                    <i className="fas fa-shield-alt me-2"></i>
                                    Autorização Pagar.me
                                </h3>
                            </div>
                            <div className="card-body">

                                {/* Gerar KYC se não existir */}
                                {!kycLink && recebedorData?.pagarme_recipient_id && (
                                    <div className="card border-info mb-4">
                                        <div className="card-body text-center">
                                            <h5 className="card-title text-info">🔗 Gerar Link de Autorização</h5>
                                            <p className="card-text">Clique para gerar seu link personalizado de autorização:</p>
                                            <button 
                                                className="btn btn-info"
                                                onClick={() => generateKycLink(recebedorData.pagarme_recipient_id)}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        Gerando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-link me-2"></i>
                                                        Gerar Link KYC
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {kycLink && (
                                    <div className="row">
                                        <div className="col">
                                            <div className="card border-primary">
                                                <div className="card-header bg-primary text-white">
                                                    <h6 className="card-title mb-0">🔗 Link de Autorização Gerado</h6>
                                                </div>
                                                <div className="card-body">
                                                    <p>Clique no botão abaixo para abrir a página de autorização segura do Pagar.me:</p>
                                                    <div className="d-flex gap-2 mb-3">
                                                        <button 
                                                            className="btn btn-success mr-2"
                                                            onClick={() => window.open(kycLink, '_blank')}
                                                        >
                                                            <i className="fas fa-external-link-alt me-2"></i>
                                                            Autorizar no Pagar.me
                                                        </button>
                                                        <button 
                                                            className="btn btn-outline-secondary"
                                                            onClick={() => navigator.clipboard.writeText(kycLink)}
                                                        >
                                                            <i className="fas fa-copy me-2"></i>
                                                            Copiar Link
                                                        </button>
                                                    </div>
                                                    <small className="text-muted">
                                                        🔒 <strong>Link seguro:</strong> {kycLink.substring(0, 50)}...
                                                    </small>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Step 4: Concluído */}
                    {step === 4 && (
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <div className="row mb-4">

                                    <div className="col">
                                        {recebedorData && (
                                            <div className="card border-info">
                                                <div className="card-header bg-info text-white">
                                                    <h6 className="card-title mb-0">📋 Informações do Recebedor</h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="mb-3">
                                                        <strong className="text-muted">Nome:</strong><br/>
                                                        <span>{recebedorData.nome}</span>
                                                    </div>
                                                    <div className="mb-3">
                                                        <strong className="text-muted">Email:</strong><br/>
                                                        <span>{recebedorData.email}</span>
                                                    </div>
                                                    <div className="mb-3">
                                                        <strong className="text-muted">ID Pagar.me:</strong><br/>
                                                        <code className="text-primary">{recebedorData.pagarme_recipient_id}</code>
                                                    </div>
                                                    <div className="mb-3">
                                                        <strong className="text-muted">Status:</strong><br/>
                                                        <span className="badge bg-success">✅ Ativo</span>
                                                    </div>
                                                    
                                                    {/* <div className="d-grid">
                                                        <button 
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={editarDados}
                                                        >
                                                            <i className="fas fa-edit me-2"></i>
                                                            Editar Dados
                                                        </button>
                                                    </div> */}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Seção de Testes */}
                                <div className="card border-warning mb-4">
                                    <div className="card-header text-dark">
                                        <h6 className="card-title mb-0">🧪 Validar Configuração</h6>
                                    </div>
                                    <div className="card-body">
                                        <p className="mb-3">Execute testes para validar se tudo está funcionando perfeitamente:</p>
                                        
                                        <div >
                                            <div>
                                                <div className="text-center">
                                                    <h6 className="text-warning">🎯 Teste de Transferência</h6>
                                                    <p className="small text-muted mb-3">Valide se o split está funcionando</p>
                                                    <button 
                                                        className="btn btn-warning"
                                                        onClick={gerarPixTeste}
                                                        disabled={loading || authorizationStatus !== 'active'}
                                                    >
                                                        {loading ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                                Gerando...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-coins me-2"></i>
                                                                PIX R$ 5,00
                                                            </>
                                                        )}
                                                    </button>
                                                    <p className="small text-muted mt-2">
                                                        Por favor, faça um pix de R$ 5,00 para validarmos se o seu split está funcionando. 
                                                    </p>
                                                </div>
                                            </div>
                                            
                    
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Modal PIX Teste */}
                    {pixTeste && (
                        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
                            <div className="modal-dialog modal-lg modal-dialog-centered">
                                <div className="modal-content">
                                    <div className="modal-header text-white">
                                        <h5 className="modal-title">
                                            <i className="fab fa-pix me-2"></i>
                                            PIX de Teste Gerado - R$ 5,00
                                        </h5>
                                        <button 
                                            type="button" 
                                            className="btn-close btn-close-white" 
                                            onClick={() => setPixTeste(null)}
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="alert alert-info mb-4">
                                            <h6><i className="fas fa-info-circle me-2"></i>Split de Teste</h6>
                                            <div className="row text-center">
                                                <div className="col-4">
                                                    <strong>Jacques (10%)</strong><br/>
                                                    <span className="text-success">R$ 0,50</span>
                                                </div>
                                                <div className="col-4">
                                                    <strong>Editora (50%)</strong><br/>
                                                    <span className="text-primary">R$ 2,50</span>
                                                </div>
                                                <div className="col-4">
                                                    <strong>Você (40%)</strong><br/>
                                                    <span className="text-warning">R$ 2,00</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="row">
                                            <div className="col-md-6 text-center">
                                                <h6 className="mb-3">QR Code PIX</h6>
                                                <div style={{ 
                                                    padding: '16px',  
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: '3px',
                                                    display: 'inline-block',
                                                    backgroundColor: '#fff'
                                                }}>
                                                    {pixTeste?.charges?.[0]?.last_transaction?.qr_code && (
                                                        <img 
                                                            src={`data:image/png;base64,${pixTeste.charges[0].last_transaction.qr_code}`}
                                                            alt="QR Code PIX"
                                                            style={{width: '200px', height: '200px'}}
                                                        />
                                                    )}
                                                </div>
                                                <p className="small text-muted mt-2">
                                                    📱 Escaneie com a câmera do seu banco
                                                </p>
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="mb-3">Código PIX</h6>
                                                <textarea
                                                    className="form-control"
                                                    value={pixTeste?.charges?.[0]?.last_transaction?.qr_code || ''}
                                                    readOnly
                                                    rows={6}
                                                    style={{ fontSize: '12px', fontFamily: 'monospace' }}
                                                />
                                                <button 
                                                    className="btn btn-outline-primary btn-sm mt-2 w-100"
                                                    onClick={copyPixCode}
                                                >
                                                    <i className="fas fa-copy me-2"></i>
                                                    Copiar Código PIX
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div className="alert alert-warning mt-4">
                                            <h6><i className="fas fa-clock me-2"></i>Aguardando Pagamento</h6>
                                            <p className="mb-0">
                                                Após a confirmação do pagamento, o split será processado automaticamente 
                                                e você poderá verificar os valores recebidos no painel administrativo.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button 
                                            type="button" 
                                            className="btn btn-outline-secondary"
                                            onClick={() => setPixTeste(null)}
                                        >
                                            Fechar
                                        </button>
                                        <a 
                                            href="https://wa.link/o4jzw4" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="btn btn-success"
                                        >
                                            <i className="fab fa-whatsapp me-2"></i>
                                            Suporte
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="d-flex justify-content-between mt-4">

                        <div className="ms-auto">

                        {step < 3 && (
                                <button 
                                    className="btn btn-primary"
                                    onClick={nextStep}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Processando...
                                        </>
                                    ) : (
                                        <>
                                            {step === 1 ? 
                                                (recebedorData ? 'Atualizar Dados' : 'Criar Recebedor') : 
                                                'Continuar'
                                            }
                                            <i className="fas fa-arrow-right ms-2"></i>
                                        </>
                                    )}
                                </button>
                            )}

                            {step === 4 && (
                                <button 
                                    className="btn btn-success"
                                    onClick={() => window.dispatchEvent(new CustomEvent('changePerfiltab', { detail: 'dashboard-autor' }))}
                                >
                                    <i className="fas fa-chart-line me-2"></i>
                                    Ver Dashboard & Vendas
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Loading Overlay */}
                    {loading && (
                        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999}}>
                            <div className="card">
                                <div className="card-body text-center">
                                    <div className="spinner-border text-primary mb-3" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <h5 className="card-title">Processando...</h5>
                                    <p className="card-text text-muted">Por favor, aguarde</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PagarmeConfig; 